import type { HeadlineResponseOrganizationResponseRelatedPagedData } from '@on3/api';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { BreadCrumb } from '@public/components/BreadCrumb/BreadCrumb';
import { ArticleFeedArticle } from '@public/components/Headlines/ArticleFeed/ArticleFeed';
import { HighSchoolNav } from '@public/components/HighSchool/Nav/HighSchoolNav';
import { On3Avatar } from '@public/components/Image/On3Avatar';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { Pagination } from '@public/components/UI/Pagination/Pagination';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import styles from './FullHighSchoolNews.module.scss';

interface IFullHighSchoolNewsProps {
  highSchoolArticles?: HeadlineResponseOrganizationResponseRelatedPagedData;
  title?: string;
}

export const FullHighSchoolNews = ({
  highSchoolArticles,
  title,
}: IFullHighSchoolNewsProps) => {
  const { list, pagination, relatedModel } = highSchoolArticles || {};
  const [pageNumber, setPageNumber] = useState(pagination?.currentPage);
  const router = useRouter();
  const hasPagination = !!pagination?.count && pagination?.count > 25;
  const primaryColor = adjustColorForReadability(
    relatedModel?.primaryColor || '#fe3b1f',
  );

  const handleChange = useCallback(
    (_event: any, value: any) => {
      setPageNumber(value);
      router.push({
        pathname: router.pathname,
        query: { ...router.query, page: value },
      });
    },
    [router],
  );

  const { slug, name = '', key } = relatedModel || {};
  const urlSlug = `${slug}-${key}`;

  return (
    <AdSidebarLayout>
      <BreadCrumb
        color={primaryColor}
        crumbs={[
          { title: 'High School', href: '/high-school/' },
          { title: name, href: `/high-school/${urlSlug}/` },
        ]}
        title="News"
      />
      <section className={styles.block}>
        <header className={styles.header}>
          <div className={styles.title}>
            <figure>
              <On3Avatar
                altText={name}
                domain={relatedModel?.defaultAsset?.domain}
                source={relatedModel?.defaultAsset?.source}
                teamAvatar
              />
            </figure>
            <h1>{title}</h1>
          </div>
          <nav style={{ color: primaryColor }}>
            <HighSchoolNav />
          </nav>
        </header>
        {hasPagination && (
          <Pagination
            color="standard"
            count={pagination?.pageCount}
            onChange={handleChange}
            page={pageNumber}
          />
        )}
        <div role="list">
          {!!list?.length &&
            list?.map((item) => (
              <ArticleFeedArticle
                {...item}
                key={item.key}
                primaryColor={primaryColor}
              />
            ))}
          {!list?.length && <span>No News</span>}
        </div>
        {hasPagination && (
          <Pagination
            color="standard"
            count={pagination?.pageCount}
            onChange={handleChange}
            page={pageNumber}
          />
        )}
      </section>
    </AdSidebarLayout>
  );
};
